import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Alert,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useDispatchWithBackdrop } from 'hooks/useDispatchWithBackdrop';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AlertDialogSlide from '../../components/Dialog/AlertDialogSlide';
import DropdownMenu from '../../components/DropdownMenu';
import MDBox from '../../components/MDBox';
import MDInput from '../../components/MDInput';
import MDTypography from '../../components/MDTypography';
import DashboardNavbar from '../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../LayoutContainers/DashboardLayout';
import {
  fetchUsersDetails,
  createUserData,
  updateUserData,
  deleteUserData,
  changeUserPassword,
} from '../../stores/sensor/sensorStore';
import { getRoleDisplay } from '../../types/roles'; // adjust path as needed
import { LAST_COMMIT_AT } from '../../generated/commitVersion';

const UserForm = () => {
  const { dispatchWithBackdrop } = useDispatchWithBackdrop();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const { users, loading } = useSelector(state => state.users);
  const currentUser = useSelector(state => state.sensors.currentUser);

  // Get user ID from URL params or determine if it's "me" route
  let { userId } = useParams();
  const isMe = window.location.pathname === '/me' || userId === 'me';

  if (userId === 'new') {
    userId = 0;
  }

  // If it's "me" route, use current user's ID
  if (isMe && currentUser?.user_id) {
    userId = currentUser.user_id;
  }

  const isAddMode = userId === 'new' || userId === 0;

  // Local state
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // User Form
  const {
    handleSubmit: handleUserSubmit,
    reset: resetEdit,
    control: controlEdit,
    watch: watchEdit,
    setValue: setValueEdit,
    formState: { errors: errorsEdit },
  } = useForm({
    defaultValues: {
      userId: 0,
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'c',
      password: '',
      passwordConfirm: '', // isAddMode
      currentPassword: '', // isMe password change
      newPassword: '', // isMe password change
      newPasswordConfirm: '', // isMe password change
    },
  });

  // Watch password fields for validation
  const watchPassword = watchEdit('password');
  const watchPasswordConfirm = watchEdit('passwordConfirm');
  const watchNewPassword = watchEdit('newPassword');
  const watchNewPasswordConfirm = watchEdit('newPasswordConfirm');

  // Find the selected user from URL params or use currentUser for /me route
  const selectedUser = useMemo(() => {
    // For /me route, use currentUser directly (it has all needed fields)
    if (isMe) {
      return {
        id: currentUser?.user_id,
        username: currentUser?.username,
        email: currentUser?.email,
        first_name: currentUser?.first_name,
        last_name: currentUser?.last_name,
        phone: currentUser?.phone,
        role: currentUser?.role,
        role_display: getRoleDisplay(currentUser?.role),
        building_ids: currentUser?.building_ids,
        full_name: currentUser?.full_name,
      };
    }

    // For regular edit, find by URL userId in users array
    if (!userId || !users || users.length === 0) return null;
    const user = users.find(user => user.id.toString() === userId.toString());
    return user;
  }, [userId, users, isMe, currentUser]);
  // Helper function to handle empty/null values
  const getValueOrDefault = value => {
    return value === null || value === undefined || value === '' ? '' : value;
  };

  const getDisplayValue = value => {
    return value === null || value === undefined || value === '' ? '-Unassigned-' : value;
  };

  // Role options based on current user's role and context
  const getRoleOptions = () => {
    // For self-editing, don't allow role changes
    if (isMe) {
      return [
        {
          name: getRoleDisplay(currentUser.role), // Use getRoleDisplay instead
          value: currentUser.role,
        },
      ];
    }

    const baseOptions = [{ name: 'Customer', value: 'c' }];

    // Only super admin can assign distributor and admin roles
    if (currentUser?.role === 's') {
      return [...baseOptions, { name: 'Distributor', value: 'd' }, { name: 'Super Admin', value: 's' }];
    }

    return baseOptions;
  };
  // Single useEffect to initialize form data
  useEffect(() => {
    if (!isAddMode && selectedUser && (isMe ? currentUser : true)) {
      const formData = {
        username: getValueOrDefault(selectedUser.username),
        email: getValueOrDefault(selectedUser.email),
        first_name: getValueOrDefault(selectedUser.first_name),
        last_name: getValueOrDefault(selectedUser.last_name),
        phone: getValueOrDefault(selectedUser.phone),
        role: getValueOrDefault(selectedUser.role),
        password: '',
      };

      resetEdit(formData);
    }
  }, [selectedUser, isAddMode, resetEdit]);

  // Load users on component mount (not needed for /me route)
  useEffect(() => {
    if (!isMe) {
      dispatch(fetchUsersDetails());
    }
  }, [dispatch, isMe]);

  // Handle User submission
  const onEditSubmitUser = async data => {
    try {
      // Handle password change for /me route
      if (isMe && showPasswordChange) {
        const passwordChangeData = {
          userId: userId,
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        };

        // You'll need to dispatch a new password change action here
        await dispatch(changeUserPassword(passwordChangeData)).unwrap();
        console.log('Password change data:', passwordChangeData); // Placeholder

        setNotification({
          open: true,
          message: 'Password updated successfully!',
          severity: 'success',
        });

        // Reset password fields and hide password change section
        setValueEdit('currentPassword', '');
        setValueEdit('newPassword', '');
        setValueEdit('newPasswordConfirm', '');
        setShowPasswordChange(false);
        return;
      }

      const userData = {
        userId: userId,
        username: data.username,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        role: data.role,
      };

      if (isAddMode) {
        await dispatch(createUserData({ ...userData, password: data.password })).unwrap();
      } else {
        await dispatch(updateUserData(userData)).unwrap();
      }

      setNotification({
        open: true,
        message: `User ${isAddMode ? 'created' : 'updated'} successfully!`,
        severity: 'success',
      });

      // Navigate back to users list after successful creation
      if (isAddMode) {
        setTimeout(() => navigate('/manage-users'), 1000);
      }
    } catch (error) {
      console.error('Operation failed:', error);
      setNotification({
        open: true,
        message: error.message || 'An unexpected error occurred',
        severity: 'error',
      });
    }
  };

  // Reset form to original user values
  const handleReset = () => {
    if (selectedUser) {
      setIsInitialLoad(true);

      const originalValues = {
        username: getValueOrDefault(selectedUser.username),
        email: getValueOrDefault(selectedUser.email),
        first_name: getValueOrDefault(selectedUser.first_name),
        last_name: getValueOrDefault(selectedUser.last_name),
        phone: getValueOrDefault(selectedUser.phone),
        role: getValueOrDefault(selectedUser.role),
        password: '',
      };

      resetEdit(originalValues);
    }
  };

  // Handle Delete User alert
  const handleDeleteClick = () => {
    if (!userId) {
      setNotification({
        open: true,
        message: 'No user selected for deletion.',
        severity: 'error',
      });
      return;
    }
    setDeleteModalOpen(true);
  };

  // User delete
  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteUserData(userId)).unwrap();

      setNotification({
        open: true,
        message: 'User deleted successfully!',
        severity: 'success',
      });

      setDeleteModalOpen(false);

      // Navigate back to users list after deletion
      setTimeout(() => navigate('/manage-users'), 1000);
    } catch (error) {
      console.error('Delete failed:', error);
      setNotification({
        open: true,
        message: error.message || 'An unexpected error occurred',
        severity: 'error',
      });
    }
  };

  const onlyDigits = s => s.replace(/\D/g, '');

  return (
    <DashboardLayout>
      <DashboardNavbar objectName={'User'} />

      {/* Header */}
      <MDBox
        my={1}
        px={2}
        py={2}
        variant="gradient"
        style={{
          background: '#3244E2',
        }}
        borderRadius="lg"
        coloredShadow="info">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" marginLeft={2} spacing={2} alignItems="end">
            <MDTypography variant="h4" color="light">
              {isMe ? 'My Profile' : isAddMode ? 'Add New User' : `Edit User: ${selectedUser?.username || selectedUser?.id}`}
            </MDTypography>
          </Stack>
        </Stack>
      </MDBox>

      <Box
        sx={{
          margin: {
            xs: '0px 0px',
            md: '0px 10rem',
            lg: '0px 22rem',
            xl: '0px 30rem',
            xxl: '0px 42rem',
          },
        }}>
        <Grid container spacing={3}>
          {/* USER INFO SECTION */}
          {selectedUser && !isAddMode && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="User Information" sx={{ color: 'white' }} />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Username:</strong> {selectedUser.username}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Role:</strong> {getRoleDisplay(selectedUser.role)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Full Name:</strong> {selectedUser.full_name || 'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Email:</strong> {getDisplayValue(selectedUser.email)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Phone:</strong> {getDisplayValue(selectedUser.phone)}
                      </Typography>
                    </Grid>
                    {selectedUser.role !== 's' && (
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Buildings Access:</strong>{' '}
                          {selectedUser.building_ids?.length || selectedUser.building_names?.length || 0}
                        </Typography>
                      </Grid>
                    )}
                    {selectedUser.role === 'd' && (
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Customer Count:</strong> {selectedUser.customer_count || 0}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {isMe && currentUser?.role === 's' && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Version" sx={{ color: 'white' }} />
                <CardContent>
                  <Typography variant="body2">
                    <strong>Last Commit:</strong> {LAST_COMMIT_AT || '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* EDIT/ADD USER SECTION */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={isMe ? 'My Profile' : isAddMode ? 'Add New User' : 'Edit User Details'}
                sx={{ bgcolor: 'white', color: 'white' }}
              />
              <CardContent>
                <form onSubmit={handleUserSubmit(onEditSubmitUser)}>
                  <Grid container spacing={3}>
                    {/* Username */}
                    <Grid item xs={6}>
                      <Controller
                        name="username"
                        control={controlEdit}
                        disabled={!isAddMode} // Username cannot be changed after creation
                        rules={{
                          required: 'Username is required',
                          minLength: {
                            value: 3,
                            message: 'Username must be at least 3 characters',
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <MDInput
                            value={value || ''}
                            label="Username"
                            placeholder="Enter username"
                            onChange={onChange}
                            fullWidth
                            required
                            disabled={!isAddMode}
                            error={!!errorsEdit.username}
                            helperText={errorsEdit.username?.message || (!isAddMode ? 'Username cannot be changed' : '')}
                            sx={{
                              //manual diabled formatting
                              ...(!isAddMode && {
                                opacity: 0.6,
                                backgroundColor: '#f5f5f5',
                                '& input': {
                                  cursor: 'not-allowed',
                                },
                              }),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={6}>
                      <Controller
                        name="email"
                        control={controlEdit}
                        rules={{
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <MDInput
                            value={value || ''}
                            label="Email"
                            placeholder="Enter email address"
                            onChange={onChange}
                            fullWidth
                            type="email"
                            error={!!errorsEdit.email}
                            helperText={errorsEdit.email?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* First Name */}
                    <Grid item xs={6}>
                      <Controller
                        name="first_name"
                        control={controlEdit}
                        render={({ field: { onChange, value } }) => (
                          <MDInput
                            value={value || ''}
                            label="First Name"
                            placeholder="Enter first name"
                            onChange={onChange}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>

                    {/* Last Name */}
                    <Grid item xs={6}>
                      <Controller
                        name="last_name"
                        control={controlEdit}
                        render={({ field: { onChange, value } }) => (
                          <MDInput
                            value={value || ''}
                            label="Last Name"
                            placeholder="Enter last name"
                            onChange={onChange}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>

                    {/* Phone */}
                    <Grid item xs={6}>
                      <Controller
                        name="phone"
                        control={controlEdit}
                        render={({ field: { onChange, value } }) => (
                          <MDInput
                            value={value || ''}
                            label="Phone Number"
                            placeholder="Enter phone number"
                            onChange={e => onChange(onlyDigits(e.target.value))}
                            onPaste={e => {
                              e.preventDefault();
                              const text = (e.clipboardData || window.clipboardData).getData('text');
                              onChange(onlyDigits(text));
                            }}
                            fullWidth
                            type="tel"
                          />
                        )}
                      />
                    </Grid>

                    {/* Role */}
                    <Grid item xs={6}>
                      <Controller
                        name="role"
                        control={controlEdit}
                        rules={{ required: 'Role is required' }}
                        render={({ field: { onChange, value } }) => (
                          <DropdownMenu
                            options={[{ name: 'Select Role', value: '' }, ...getRoleOptions()]}
                            selectedOption={value || ''}
                            handleChange={onChange}
                            error={!!errorsEdit.role}
                            helperText={errorsEdit.role?.message}
                            disabled={currentUser?.role === 'd' || isMe} // Distributors can only create customers, users can't change their own role
                            sx={{
                              fontSize: '0.75rem',
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {isAddMode && (
                      <>
                        <Grid item xs={6}>
                          <Controller
                            name="password"
                            control={controlEdit}
                            rules={{
                              required: 'Password is required',
                              minLength: {
                                value: 6,
                                message: 'Password must be at least 6 characters',
                              },
                            }}
                            render={({ field: { onChange, value } }) => (
                              <MDInput
                                value={value || ''}
                                label="Password"
                                placeholder="Enter password"
                                onChange={onChange}
                                fullWidth
                                type="password"
                                required
                                error={!!errorsEdit.password}
                                helperText={errorsEdit.password?.message}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <Controller
                            name="passwordConfirm"
                            control={controlEdit}
                            rules={{
                              required: 'Please confirm password',
                              validate: value => value === watchPassword || 'Passwords do not match',
                            }}
                            render={({ field: { onChange, value } }) => (
                              <MDInput
                                value={value || ''}
                                label="Confirm Password"
                                placeholder="Confirm password"
                                onChange={onChange}
                                fullWidth
                                type="password"
                                required
                                error={!!errorsEdit.passwordConfirm}
                                helperText={errorsEdit.passwordConfirm?.message}
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Password Change Section for /me route */}
                    {isMe && (
                      <>
                        {/* Password Change Checkbox */}
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showPasswordChange}
                                onChange={e => {
                                  setShowPasswordChange(e.target.checked);
                                  if (!e.target.checked) {
                                    // Clear password fields when unchecking
                                    setValueEdit('currentPassword', '');
                                    setValueEdit('newPassword', '');
                                    setValueEdit('newPasswordConfirm', '');
                                  }
                                }}
                                color="primary"
                              />
                            }
                            label="Change Password"
                          />
                        </Grid>

                        {/* Password Change Fields - Show only when checkbox is checked */}
                        {showPasswordChange && (
                          <>
                            <Grid item xs={4}>
                              <Controller
                                name="currentPassword"
                                control={controlEdit}
                                rules={{
                                  required: 'Current password is required',
                                }}
                                render={({ field: { onChange, value } }) => (
                                  <MDInput
                                    value={value || ''}
                                    label="Current Password"
                                    placeholder="Enter current password"
                                    onChange={onChange}
                                    fullWidth
                                    type="password"
                                    required
                                    error={!!errorsEdit.currentPassword}
                                    helperText={errorsEdit.currentPassword?.message}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <Controller
                                name="newPassword"
                                control={controlEdit}
                                rules={{
                                  required: 'New password is required',
                                  minLength: {
                                    value: 6,
                                    message: 'Password must be at least 6 characters',
                                  },
                                }}
                                render={({ field: { onChange, value } }) => (
                                  <MDInput
                                    value={value || ''}
                                    label="New Password"
                                    placeholder="Enter new password"
                                    onChange={onChange}
                                    fullWidth
                                    type="password"
                                    required
                                    error={!!errorsEdit.newPassword}
                                    helperText={errorsEdit.newPassword?.message}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <Controller
                                name="newPasswordConfirm"
                                control={controlEdit}
                                rules={{
                                  required: 'Please confirm new password',
                                  validate: value => value === watchNewPassword || 'Passwords do not match',
                                }}
                                render={({ field: { onChange, value } }) => (
                                  <MDInput
                                    value={value || ''}
                                    label="Confirm New Password"
                                    placeholder="Confirm new password"
                                    onChange={onChange}
                                    fullWidth
                                    type="password"
                                    required
                                    error={!!errorsEdit.newPasswordConfirm}
                                    helperText={errorsEdit.newPasswordConfirm?.message}
                                  />
                                )}
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}

                    {/* Submit Buttons */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2}>
                        <Button type="submit" variant="contained" color="primary" disabled={loading}>
                          {isAddMode ? 'Add User' : 'Update User'}
                        </Button>
                        <Button variant="outlined" onClick={handleReset} sx={{ borderColor: 'blue', color: 'blue' }}>
                          Reset
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* MANAGE PERMISSIONS SECTION - Hidden for admins, new users, and self-editing */}
          {selectedUser && selectedUser.role !== 's' && !isAddMode && !isMe && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Permissions" sx={{ color: 'white' }} />
                <CardContent>
                  <Typography variant="body1" gutterBottom>
                    Manage building and area permissions for this user.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate(`/manage-permissions/users/${selectedUser.id || userId}`)}
                    sx={{ mt: 2 }}>
                    Manage Permissions
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* DELETE USER SECTION - Hidden for self-editing */}
          {selectedUser && !isAddMode && !isMe && (
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Delete User"
                  sx={{
                    bgcolor: 'error.main',
                    '& .MuiCardHeader-title': { color: '#ffffff' },
                  }}
                />
                <CardContent>
                  <Typography variant="body1" gutterBottom>
                    Permanently delete this user from the system. This action cannot be undone.
                  </Typography>
                  <Button variant="contained" onClick={handleDeleteClick} disabled={loading} sx={{ mt: 2, background: 'red' }}>
                    Delete User
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Back Button */}
          <Grid item xs={12}>
            <Button variant="text" onClick={() => navigate(isMe ? '/home' : '/manage-users')} sx={{ mt: 2 }}>
              ← Back to {isMe ? 'Dashboard' : 'Users List'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Modal */}
      <AlertDialogSlide
        open={deleteModalOpen}
        title="Confirm User Deletion"
        content={
          <Box sx={{ padding: '8px', fontWeight: 'bold', width: '500px' }}>
            Are you sure you want to delete user "{selectedUser?.username}"?
            <br />
            <br />
            This action will:
            <br />
            → Remove user access to all buildings
            <br />→ Delete all user permissions
            <br />→ Remove user from the system permanently
            <br />
            <br />
            This action cannot be undone.
          </Box>
        }
        handleClose={() => setDeleteModalOpen(false)}
        handleOk={handleDeleteConfirm}
      />

      {/* Notification Snackbar */}
      <CustomSnackbar
        open={notification.open}
        duration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        vertical="top"
        horizontal="right"
        severity={notification.severity}
        message={notification.message}
      />
    </DashboardLayout>
  );
};

export default UserForm;
